import { useDispatch } from "react-redux";
import {
  AnimatedCard,
  AnimatedCardProps,
} from "@/shared/base/components/AnimatedCard/AnimatedCard";
import { Box, Button, Divider, Stack, Typography } from "@mui/material";

import {
  InventoryCardPerks,
  InventoryCardListItem,
} from "./inventoryCard.components";
import { InventoryTag } from "./inventoryCard.helpers";

import { seatMapState } from "@/global/store/data/seatMap";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { getUSCurrency } from "@/global/utils/getUSCurrency";
import {
  calculateSuiteName,
  calculateTicketPlusPerksPrice,
} from "../utils.chooseSeats";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";

import type {
  InventoryType,
  SuitLevelV2Inventory,
} from "@/global/store/data/types";

interface Props {
  cardVariant?: AnimatedCardProps["variant"];
  inventory: InventoryType;
  isSelected?: boolean;
  isDisabled?: boolean;
  isDisabledHoverAnim?: boolean;
  isFooterHidden?: boolean;
  onSelect?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onRender?: (el: HTMLElement) => void;
  style?: AnimatedCardProps["style"];
}

export function SuiteLevelV2InventoryCard(props: Props) {
  const dispatch = useDispatch();
  const inventory = props.inventory as SuitLevelV2Inventory;
  const teamConfig = useTeamConfig();
  const { seatMapConfigs } = teamConfig;
  const { suiteImages } = seatMapConfigs;
  const { sm: smDown } = useCssMediaQuery("down");
  const { inventoryCardSettings } = teamConfig.uiConfigs;

  // Does this section have its own carousel images?
  // array form = same images for all sections; object form = per-section entry.
  const sectionHasImages = Array.isArray(suiteImages)
    ? suiteImages.length > 0
    : !!suiteImages?.[inventory?.mapSectionName ?? ""]?.length;

  // The carousel still has a default 3D/2D slide unless it's fully suppressed.
  // `hide3dViewFromSlider === true` removes it for every section; the object
  // form only hides specific sections, so treat it as "slide may exist".
  const hasDefaultSeatMapSlide =
    seatMapConfigs?.hide3dViewFromSlider !== true ||
    !!seatMapConfigs?.isGroupTicketSeatMapEnabled;

  // Show "View Suite" only when the carousel would render something — either
  // this section's images or a default map slide. Avoids an empty carousel for
  // sections intentionally left out of `suiteImages` (e.g. SOCIAL).
  const hasSuiteImages =
    !!suiteImages && (sectionHasImages || hasDefaultSeatMapSlide);

  const totalAllInPriceWithPerks = calculateTicketPlusPerksPrice(inventory);

  // prettier-ignore
  const trimmedSuiteName = inventory?.suiteName?.trim() || "";
  // prettier-ignore
  const isButtonVisible = trimmedSuiteName &&
    inventoryCardSettings?.suiteLevelInventoryCard?.customButtons?.some(
      (button) => button.suiteNames?.includes(trimmedSuiteName)
    );

  // prettier-ignore
  const footerHeight = isButtonVisible ?
    `${(inventoryCardSettings?.suiteLevelInventoryCard?.customButtons?.length || 0) * 20 + 50}px`
    : "70px";

  const handleViewSuite = (e: any) => {
    e.stopPropagation();
    if (teamConfig?.seatMapConfigs?.isIoMedia3dViewEnabled) {
      dispatch(
        seatMapState.actions.set({
          viewId3d: inventory?.mapSectionName,
        })
      );
    } else {
      dispatch(
        seatMapState.actions.set({
          viewId3d: `S_${inventory?.mapSectionName}`,
        })
      );
    }
  };

  return (
    <AnimatedCard
      variant={props?.cardVariant}
      style={props?.style}
      onRender={props?.onRender}
      onMouseEnter={props.onMouseEnter}
      onMouseLeave={props.onMouseLeave}
      borderRadius={"10px"}
      footerHeight={footerHeight}
      isSelected={props?.isSelected}
      isDisabled={props?.isDisabled}
      isDisabledHoverAnim={props?.isDisabledHoverAnim}
      isFooterHidden={props?.isFooterHidden}
      renderBody={() => {
        return (
          <Box onClick={props.onSelect}>
            <Stack
              sx={{
                flexDirection: "row",
                position: "absolute",
                top: 0,
                right: "8px",
                zIndex: 1,
                transform: "translate(0%, -50%) scale(1)",
                overflow: "hidden",
                gap: 1,
              }}
            >
              {inventory?.containsAisle ? (
                <InventoryTag value={"Aisle Seat"} />
              ) : null}

              {inventory.sectionNameTag ? (
                <InventoryTag value={inventory.sectionNameTag} />
              ) : null}
            </Stack>

            <Box p={2} py={2.2}>
              <Stack
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
                width={"100%"}
              >
                <Typography
                  variant="body2"
                  fontWeight={700}
                  color="text.primary"
                  fontSize={smDown ? "0.75rem" : "0.875rem"}
                  // if text is too long, decrease font size
                  sx={{
                    fontSize:
                      inventory?.suiteName && inventory?.suiteName?.length > 26
                        ? "0.75rem"
                        : "0.875rem",
                  }}
                >
                  {inventory?.suiteName
                    ? `${calculateSuiteName(inventory?.suiteName, teamConfig)}`
                    : `Section ${inventory.sectionName}`}
                </Typography>

                {hasSuiteImages ? (
                  <Button
                    sx={{
                      py: 0.5,
                      px: 1,
                      fontSize: smDown ? "0.75rem" : "0.875rem",
                      lineHeight: 1.3,
                      minWidth: "88.33px",
                    }}
                    variant="contained"
                    onClick={handleViewSuite}
                  >
                    {inventoryCardSettings?.viewSuiteButtonLabel ||
                      "View Suite"}
                  </Button>
                ) : null}
              </Stack>
            </Box>

            <Divider />

            <Stack padding={"1rem"}>
              {!inventoryCardSettings?.hideSeatCount ? (
                <InventoryCardListItem
                  label={inventoryCardSettings?.seatCountLabel || "Seat Count: "}
                  value={inventory?.displayStandardSeatCount}
                />
              ) : null}

              <InventoryCardPerks inventory={inventory} />

              <InventoryCardListItem
                label={inventoryCardSettings?.totalPriceLabel || "Total Price:"}
                value={getUSCurrency(totalAllInPriceWithPerks)}
              />
            </Stack>

            {inventoryCardSettings?.suiteLevelInventoryCardFooterText?.[
              inventory?.mapSectionName || inventory?.suiteName || ""
            ] ? (
              <Stack paddingX={"1rem"} paddingBottom={"1rem"}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontSize={"0.75rem"}
                  fontStyle={"italic"}
                >
                  {
                    inventoryCardSettings?.suiteLevelInventoryCardFooterText?.[
                      inventory?.mapSectionName ||
                        (inventory?.suiteName as string)
                    ]
                  }
                </Typography>
              </Stack>
            ) : null}
          </Box>
        );
      }}
      renderFooter={() => {
        return (
          <Box
            p={2}
            gap={1}
            width={"100%"}
            display={"flex"}
            flexDirection={"column"}
          >
            <Button
              variant={props.isSelected ? "outlined" : "contained"}
              onClick={props.onSelect}
              sx={{ width: "100%" }}
            >
              {props.isSelected ? "Unselect" : "Select +"}
            </Button>

            {isButtonVisible &&
              inventoryCardSettings?.suiteLevelInventoryCard?.customButtons
                ?.filter((item) =>
                  item.suiteNames?.includes(trimmedSuiteName)
                )
                ?.map((item) => {
                  return (
                    <Button
                      key={item.id}
                      variant="outlined"
                      sx={{ width: "100%" }}
                      onClick={item.onClick}
                    >
                      {item.label}
                    </Button>
                  );
                })}
          </Box>
        );
      }}
    />
  );
}
